import { Divider } from "@mui/material";
import {
  FC,
  MouseEventHandler,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { AccessControl } from "@ryde/controls";
import {
  useDropdownSourceContext,
  useRidesRecalculatePrice,
  useRtl,
} from "@ryde/redux";
import {
  ActionsEnum,
  currencySignSelector,
  formatPrice,
  IPriceDetailLine,
  IRideDto,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  SINGLE_KEY,
} from "@ryde/shared";

import {
  buildDisplayedPriceDetails,
  shouldDisplaySupplierRidePriceBreakdownLines,
} from "./ride-header-price-dropdown.utils";
import { RideHeaderPriceSupplierDropdownStyled } from "./ride-header.styled";

interface IRideHeaderPriceSupplierDropdown {
  rideDto?: Partial<IRideDto>;
  handleSupplierPriceRateDrawerOpen: (id: number) => Promise<void>;
}

export const RideHeaderPriceSupplierDropdown: FC<
  IRideHeaderPriceSupplierDropdown
> = ({ rideDto, handleSupplierPriceRateDrawerOpen }) => {
  const { t, i18n } = useTranslation();
  const { getDropdownLabel } = useDropdownSourceContext();
  const { handleRecalculateSupplierPriceClick } = useRidesRecalculatePrice();
  const [supplierPriceRateName, setSupplierPriceRateName] =
    useState(NO_VALUE_DISPLAY);
  const currencySign = useRecoilValue(currencySignSelector);
  const { isRtl } = useRtl();
  const [, startTransition] = useTransition();
  const priceDir = isRtl ? "ltr" : undefined;

  const onDropdownOpenClick: MouseEventHandler<SVGSVGElement> = e => {
    e.preventDefault();

    const supplierPriceRateId = rideDto?.supplierPriceRateId;
    if (!supplierPriceRateId) {
      return;
    }

    handleSupplierPriceRateDrawerOpen(supplierPriceRateId || 0);
  };

  useEffect(() => {
    const loadSupplierPriceRateName = async (): Promise<void> => {
      const supplierPriceRateId = rideDto?.supplierPriceRateId;
      if (!supplierPriceRateId) {
        setSupplierPriceRateName(NO_VALUE_DISPLAY);
      } else {
        const supplierPriceRateLabel = await getDropdownLabel(
          "supplierPriceRate",
          supplierPriceRateId,
        );

        setSupplierPriceRateName(supplierPriceRateLabel);
      }
    };

    startTransition(() => {
      loadSupplierPriceRateName();
    });
  }, [getDropdownLabel, rideDto]);

  const isRecalculatePriceButtonDisplayed = Boolean(
    rideDto?.metadata?.stationIds?.length,
  );

  const displayedPriceDetails: IPriceDetailLine[] = useMemo(() => {
    if (!shouldDisplaySupplierRidePriceBreakdownLines(rideDto)) {
      return [];
    }
    return buildDisplayedPriceDetails(rideDto?.metadata?.supplierPriceDetails);
  }, [rideDto]);

  return (
    rideDto && (
      <RideHeaderPriceSupplierDropdownStyled>
        <div className="headline">
          <span className="priceRateText">
            {t(`supplierPriceRate.${SINGLE_KEY}`)}{" "}
            {supplierPriceRateName ||
              rideDto?.supplierPriceRateId ||
              NO_VALUE_DISPLAY}
          </span>
          {!!rideDto?.supplierPriceRateId && (
            <AccessControl entity="supplierPriceRate" action={ActionsEnum.Read}>
              <SVG_MUI.SquareIconUpRight4SVG
                className="linkOut"
                onClick={onDropdownOpenClick}
              />
            </AccessControl>
          )}
        </div>

        {(typeof rideDto?.metadata?.supplierManualPrice === "number" ||
          !!rideDto?.metadata?.supplierManualPrice) && (
          <div className="price_row">
            <span>{t("ride.formLabel.supplierManualPrice")}</span>
            <span className="price" dir={priceDir}>
              {`${currencySign}${formatPrice({
                value: rideDto?.metadata?.supplierManualPrice ?? 0,
              })}`}
            </span>
          </div>
        )}

        {displayedPriceDetails?.map((row, idx) => {
          const labelKey = `ride.formLabel.${row.label}`;
          const labelText = i18n.exists(labelKey) ? t(labelKey) : row.label;
          return (
            <div className="price_row" key={`${row.label}-${idx}`}>
              <span>{labelText}</span>
              {!!row.calcDetails && ` (${row.calcDetails})`}
              <span className="price" dir={priceDir}>
                {`${currencySign}${formatPrice({ value: row.value })}`}
              </span>
            </div>
          );
        })}

        <div className="total_row">
          <span>{t("ride.formLabel.supplierPrice")}</span>
          <span className="total" dir={priceDir}>
            {`${currencySign}${formatPrice({
              value: rideDto?.supplierPrice ?? 0,
            })}`}
          </span>
        </div>

        <AccessControl
          entity="system"
          action={ActionsEnum.UpdateSupplierPrices}
        >
          {isRecalculatePriceButtonDisplayed && (
            <>
              <Divider className="divider" orientation="horizontal" />
              <div className="recalculate_price_row">
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleRecalculateSupplierPriceClick}
                  onKeyDown={onActivateKeyDown(
                    handleRecalculateSupplierPriceClick,
                  )}
                  className="recalculate_price_text"
                >
                  {t(`general.RECALCULATE_SUPPLIER_PRICE`)}
                </span>
              </div>
            </>
          )}
        </AccessControl>
      </RideHeaderPriceSupplierDropdownStyled>
    )
  );
};
